import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDepartmentIdFromToken, getToken } from '../services/authService';
import { fetchHiringRequests } from '../services/apiService';

const CandidateCardsList = ({
  candidates,
  departments,
  demandPlans,
  showStatusFilter = true,
  fixedStatus = '',
  isEmbedded = false,
}) => {
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [hiringRequestFilter, setHiringRequestFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [hiringRequests, setHiringRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const roles = useMemo(() => {
    const roleSet = new Set(demandPlans.map(dp => dp.role));
    return Array.from(roleSet);
  }, [demandPlans]);

  const loadHiringRequests = async () => {
    try {
      setLoading(true);
      const userRole = getToken();
      const departmentId = getDepartmentIdFromToken();
      const requests = await fetchHiringRequests();

      let filteredRequests = [...requests];

      if (userRole?.IsDepartmentRestricted === "True") {
        filteredRequests = filteredRequests.filter(
          req => String(req.department_id) === String(departmentId)
        );
      } else {
        filteredRequests = filteredRequests.filter(req => {
          return req.isDepartmentRestricted
            ? req.status !== "Draft"
            : true;
        });
      }

      setHiringRequests(filteredRequests);
    } catch (err) {
      console.error(err);
      setError("Failed to load hiring requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHiringRequests();
  }, []);

  const filteredCandidates = useMemo(() => {
    const filtered = candidates.filter(candidate => {
      const matchesDept = departmentFilter
        ? candidate.department_id === Number(departmentFilter)
        : true;

      const matchesRole = roleFilter
        ? candidate.role?.toLowerCase() === roleFilter.toLowerCase()
        : true;

      const statusToMatch = fixedStatus || statusFilter;

      const matchesStatus = Array.isArray(statusToMatch)
        ? statusToMatch.map(s => s.toLowerCase()).includes(candidate.status?.toLowerCase())
        : statusToMatch
          ? candidate.status?.toLowerCase() === statusToMatch.toLowerCase()
          : true;

      const matchesSearch = search
        ? candidate.full_name?.toLowerCase().includes(search.toLowerCase())
        : true;

      const matchesHiringRequest = hiringRequestFilter
        ? String(candidate.request_id) === String(hiringRequestFilter)
        : true;

      return matchesDept && matchesRole && matchesStatus && matchesSearch && matchesHiringRequest;
    });

    setCurrentPage(1);
    return filtered;
  }, [candidates, departmentFilter, roleFilter, statusFilter, search, fixedStatus, hiringRequestFilter]);

  const paginatedCandidates = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCandidates.slice(start, start + itemsPerPage);
  }, [filteredCandidates, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);

  // Status badge styling function
  const getStatusStyle = (status) => {
    const styles = {
      'Applied': { bg: 'rgba(18, 76, 114, 0.1)', text: 'rgb(18, 76, 114)', border: 'rgba(18, 76, 114, 0.2)' },
      'Send For Initial Approval': { bg: 'rgba(168, 85, 247, 0.1)', text: 'rgb(147, 51, 234)', border: 'rgba(168, 85, 247, 0.2)' },
      'Approved By Hiring Manager': { bg: 'rgba(34, 197, 94, 0.1)', text: 'rgb(22, 163, 74)', border: 'rgba(34, 197, 94, 0.2)' },
      'Shortlisted For Interview': { bg: 'rgba(59, 130, 246, 0.1)', text: 'rgb(37, 99, 235)', border: 'rgba(59, 130, 246, 0.2)' },
      'Rejected': { bg: 'rgba(239, 68, 68, 0.1)', text: 'rgb(220, 38, 38)', border: 'rgba(239, 68, 68, 0.2)' },
      'On Hold': { bg: 'rgba(245, 158, 11, 0.1)', text: 'rgb(217, 119, 6)', border: 'rgba(245, 158, 11, 0.2)' },
      'Interview Scheduled': { bg: 'rgba(139, 92, 246, 0.1)', text: 'rgb(124, 58, 237)', border: 'rgba(139, 92, 246, 0.2)' },
      'Under Evaluation': { bg: 'rgba(249, 115, 22, 0.1)', text: 'rgb(234, 88, 12)', border: 'rgba(249, 115, 22, 0.2)' },
      'ShortListed': { bg: 'rgba(16, 185, 129, 0.1)', text: 'rgb(5, 150, 105)', border: 'rgba(16, 185, 129, 0.2)' },
      'Under Hiring Process': { bg: 'rgba(59, 130, 246, 0.1)', text: 'rgb(37, 99, 235)', border: 'rgba(59, 130, 246, 0.2)' },
      'Send For Approval': { bg: 'rgba(168, 85, 247, 0.1)', text: 'rgb(147, 51, 234)', border: 'rgba(168, 85, 247, 0.2)' },
      'Approved': { bg: 'rgba(34, 197, 94, 0.1)', text: 'rgb(22, 163, 74)', border: 'rgba(34, 197, 94, 0.2)' },
      'Hired': { bg: 'rgba(16, 185, 129, 0.1)', text: 'rgb(5, 150, 105)', border: 'rgba(16, 185, 129, 0.2)' },
    };
    return styles[status] || { bg: 'rgba(18, 76, 114, 0.1)', text: 'rgb(18, 76, 114)', border: 'rgba(18, 76, 114, 0.2)' };
  };

  return (
   <div className={`relative ${isEmbedded ? '' : 'mb-6 -top-[180px]'}`}>
      <div className="bg-white rounded-2xl shadow-2xl" style={{ 
        border: '1px solid rgba(18, 76, 114, 0.1)'
      }}>
        {/* 🔍 Filters Section */}
        <div className="p-6 border-b" style={{ 
          borderColor: 'rgba(18, 76, 114, 0.1)',
          background: 'linear-gradient(to right, rgba(18, 76, 114, 0.03), transparent)'
        }}>
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 mb-4">
            <div className="w-full lg:w-auto">
              <h2 className="text-xl font-semibold" style={{ color: 'rgb(18, 76, 114)' }}>
                Candidates
              </h2>
              <p className="text-sm mt-1" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''} found
              </p>
            </div>
            
            <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
              <span>Show:</span>
              <div className="relative">
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="pl-3 pr-8 py-1.5 rounded-lg outline-none transition text-gray-700 appearance-none"
                  style={{ 
                    border: '1px solid rgba(18, 76, 114, 0.3)',
                    backgroundColor: 'white',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.2em 1.2em'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                    e.target.style.borderColor = 'rgb(18, 76, 114)';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                  }}
                >
                  {[9, 12, 18, 24].map((count) => (
                    <option key={count} value={count}>
                      {count} per page
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search Input */}
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Search Candidate
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 rounded-lg outline-none transition text-gray-700"
                  style={{ 
                    border: '1px solid rgba(18, 76, 114, 0.3)',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                    e.target.style.borderColor = 'rgb(18, 76, 114)';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                  }}
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <svg className="w-4 h-4" style={{ color: 'rgba(18, 76, 114, 0.5)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Department Filter */}
            {showStatusFilter && (
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                  Department
                </label>
                <div className="relative">
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="w-full pl-3 pr-8 py-2.5 rounded-lg outline-none transition text-gray-700 appearance-none"
                    style={{ 
                      border: '1px solid rgba(18, 76, 114, 0.3)',
                      backgroundColor: 'white',
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.2em 1.2em'
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                      e.target.style.borderColor = 'rgb(18, 76, 114)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                    }}
                  >
                    <option value="">All Departments</option>
                    {departments.map((dept) => (
                      <option key={dept.department_id} value={dept.department_id}>
                        {dept.department_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Hiring Request Filter */}
            {showStatusFilter && (
              <div className="space-y-1">
                <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                  Hiring Request
                </label>
                <div className="relative">
                  <select
                    value={hiringRequestFilter}
                    onChange={e => setHiringRequestFilter(e.target.value)}
                    className="w-full pl-3 pr-8 py-2.5 rounded-lg outline-none transition text-gray-700 appearance-none"
                    style={{ 
                      border: '1px solid rgba(18, 76, 114, 0.3)',
                      backgroundColor: 'white',
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 0.5rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.2em 1.2em'
                    }}
                    onFocus={(e) => {
                      e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                      e.target.style.borderColor = 'rgb(18, 76, 114)';
                    }}
                    onBlur={(e) => {
                      e.target.style.boxShadow = 'none';
                      e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                    }}
                  >
                    <option value="">All Hiring Requests</option>
                    {hiringRequests
                      .filter(req => 
                        (departmentFilter ? String(req.department_id) === String(departmentFilter) : true) &&
                        !["Draft", "Submitted", "Approved"].includes(req.status)
                      )
                      .map(req => (
                        <option key={req.request_id} value={req.request_id}>
                          {req.referenceNumber || `Request #${req.request_id}`}
                        </option>
                      ))
                    }
                  </select>
                </div>
              </div>
            )}

            {/* Status Filter */}
            <div className="space-y-1">
              <label className="block text-sm font-medium" style={{ color: 'rgb(18, 76, 114)' }}>
                Status
              </label>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-3 pr-8 py-2.5 rounded-lg outline-none transition text-gray-700 appearance-none"
                  style={{ 
                    border: '1px solid rgba(18, 76, 114, 0.3)',
                    backgroundColor: 'white',
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23124c72' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.5rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.2em 1.2em'
                  }}
                  onFocus={(e) => {
                    e.target.style.boxShadow = '0 0 0 2px rgba(18, 76, 114, 0.2)';
                    e.target.style.borderColor = 'rgb(18, 76, 114)';
                  }}
                  onBlur={(e) => {
                    e.target.style.boxShadow = 'none';
                    e.target.style.borderColor = 'rgba(18, 76, 114, 0.3)';
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="Applied">Applied</option>
                  <option value="Send For Initial Approval">Initial Approval</option>
                  <option value="Approved By Hiring Manager">Hiring Manager Approved</option>
                  <option value="Shortlisted For Interview">Interview Shortlisted</option>
                  <option value="Rejected">Rejected</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Interview Scheduled">Interview Scheduled</option>
                  <option value="Under Evaluation">Under Evaluation</option>
                  <option value="ShortListed">ShortListed</option>
                  <option value="Under Hiring Process">Under Hiring Process</option>
                  <option value="Send For Approval">Send For Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Hired">Hired</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* 👤 Candidate Cards */}
        <div className="p-6">
          {paginatedCandidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(18, 76, 114, 0.1)' }}>
                <svg className="w-12 h-12" style={{ color: 'rgba(18, 76, 114, 0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: 'rgb(18, 76, 114)' }}>No Candidates Found</h3>
              <p className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                Try adjusting your search or filter criteria
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedCandidates.map((candidate) => {
                const candidateId = candidate?.candidate_id;
                if (!candidateId) return null;

                const department = departments.find(d => d.department_id === candidate.department_id);
                const statusStyle = getStatusStyle(candidate.status);

                return (
                  <Link
                    to="/candidate-details"
                    state={{ id: candidateId }}
                    key={candidateId}
                    className="group block bg-white rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-rgb(18, 76, 114) no-underline"
                    style={{ 
                      border: '1px solid rgba(18, 76, 114, 0.1)',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(18, 76, 114, 0.1)';
                      e.currentTarget.style.borderColor = 'rgb(18, 76, 114)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(18, 76, 114, 0.1)';
                    }}
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        {/* Candidate Avatar */}
                        <div className="relative">
                          <img
                            src={
                              candidate.profile_picture ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(candidate.full_name)}&background=124c72&color=fff&bold=true`
                            }
                            alt={candidate.full_name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md"
                          />
                          {/* Status Indicator */}
                          <div 
                            className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white"
                            style={{ backgroundColor: statusStyle.text }}
                          />
                        </div>

                        {/* Candidate Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold truncate mb-1" style={{ color: 'rgb(18, 76, 114)' }}>
                            {candidate.full_name}
                          </h3>
                          

                          {/* Status Badge */}
                          <div 
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mb-4"
                            style={{
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.text,
                              border: `1px solid ${statusStyle.border}`
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {candidate.status}
                          </div>

                          {/* Department Info */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(18, 76, 114, 0.6)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                              <span className="text-sm truncate" style={{ color: 'rgba(18, 76, 114, 0.8)' }}>
                                {department?.department_name || 'No department'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* View Details Button */}
                      <div className="mt-5 pt-4 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.6)' }}>
                            View details →
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* 📄 Pagination */}
        {totalPages > 1 && (
          <div className="p-6 border-t" style={{ borderColor: 'rgba(18, 76, 114, 0.1)' }}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm" style={{ color: 'rgba(18, 76, 114, 0.7)' }}>
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredCandidates.length)}</span> of{' '}
                <span className="font-medium">{filteredCandidates.length}</span> candidates
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg transition-all font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ 
                    color: 'rgb(18, 76, 114)',
                    border: '1px solid rgba(18, 76, 114, 0.3)',
                    backgroundColor: currentPage === 1 ? 'rgba(18, 76, 114, 0.05)' : 'white'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== 1) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    if (pageNum < 1 || pageNum > totalPages) return null;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all font-medium ${
                          currentPage === pageNum
                            ? 'text-white'
                            : 'text-rgb(18, 76, 114) hover:bg-rgba(18, 76, 114, 0.1)'
                        }`}
                        style={currentPage === pageNum ? {
                          backgroundColor: 'rgb(18, 76, 114)',
                          boxShadow: '0 2px 4px rgba(18, 76, 114, 0.2)'
                        } : {
                          color: 'rgb(18, 76, 114)',
                          border: '1px solid rgba(18, 76, 114, 0.2)'
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg transition-all font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ 
                    color: 'rgb(18, 76, 114)',
                    border: '1px solid rgba(18, 76, 114, 0.3)',
                    backgroundColor: currentPage === totalPages ? 'rgba(18, 76, 114, 0.05)' : 'white'
                  }}
                  onMouseEnter={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = 'rgba(18, 76, 114, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentPage !== totalPages) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateCardsList;